/**
 * StructuredRequest Core Utilities
 * Shared validation and helper functions
 */

import type { FieldDefinition, RequestTemplate } from './models.js';

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generate a unique ID using crypto API
 */
export function generateId(prefix?: string): string {
  const timestamp = Date.now().toString(36);
  const randomPart =
    typeof crypto !== 'undefined'
      ? crypto.randomUUID().split('-')[0]
      : Math.random().toString(36).substring(2, 10);

  return prefix ? `${prefix}_${timestamp}${randomPart}` : `${timestamp}${randomPart}`;
}

/**
 * Generate a document ID as a prefixed UUID.
 * Format: "{type}_{uuid}" e.g. "req_550e8400-e29b-41d4-a716-446655440000"
 * Tenant isolation is enforced by partition keys and document fields, not the ID.
 */
export function generateDocId(type: string): string {
  const uuid =
    typeof crypto !== 'undefined'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).substring(2)}`;
  return `${type}_${uuid}`;
}

// ============================================================================
// String Utilities
// ============================================================================

/**
 * Convert a label to camelCase for use as field name
 */
export function toCamelCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^./, (chr) => chr.toLowerCase())
    .replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Convert a label to a URL-safe slug
 */
export function toSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ============================================================================
// Validation
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s\-+()]{7,20}$/;

/**
 * Validate a single field value
 */
export function validateField(
  field: FieldDefinition,
  value: unknown
): string | null {
  // Required check
  if (field.required) {
    if (value === undefined || value === null || value === '') {
      return `${field.label} is required`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return `${field.label} is required`;
    }
  }

  // Skip further validation if empty and not required
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const strValue = String(value);
  const validation = field.validation;

  // Type-specific validation
  switch (field.type) {
    case 'email':
      if (!EMAIL_REGEX.test(strValue)) {
        return 'Please enter a valid email address';
      }
      break;

    case 'phone':
      if (!PHONE_REGEX.test(strValue)) {
        return 'Please enter a valid phone number';
      }
      break;

    case 'number':
      if (isNaN(Number(value))) {
        return 'Please enter a valid number';
      }
      if (validation?.min !== undefined && Number(value) < validation.min) {
        return `Value must be at least ${validation.min}`;
      }
      if (validation?.max !== undefined && Number(value) > validation.max) {
        return `Value must be at most ${validation.max}`;
      }
      break;
  }

  // String length validation
  if (typeof value === 'string' && validation) {
    if (validation.minLength && strValue.length < validation.minLength) {
      return `Must be at least ${validation.minLength} characters`;
    }
    if (validation.maxLength && strValue.length > validation.maxLength) {
      return `Must be at most ${validation.maxLength} characters`;
    }
    if (validation.pattern) {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(strValue)) {
        return validation.patternMessage || 'Invalid format';
      }
    }
  }

  return null;
}

/**
 * Validate all fields in a form
 */
export function validateForm(
  fields: FieldDefinition[],
  data: Record<string, unknown>
): ValidationResult {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    if (field.type === 'spacer') continue;

    if (field.type === 'repeater') {
      const rows = data[field.name];
      if (field.required && (!Array.isArray(rows) || rows.length === 0)) {
        errors[field.name] = `${field.label} requires at least one row`;
      } else if (Array.isArray(rows) && field.subFields?.length) {
        rows.forEach((row: Record<string, unknown>, rowIdx: number) => {
          field.subFields!.forEach((sub) => {
            if (sub.required && (row[sub.name] === undefined || row[sub.name] === null || row[sub.name] === '')) {
              errors[`${field.name}[${rowIdx}].${sub.name}`] = `Row ${rowIdx + 1}: ${sub.label} is required`;
            }
          });
        });
      }
      continue;
    }

    const error = validateField(field, data[field.name]);
    if (error) {
      errors[field.name] = error;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

// ============================================================================
// Computed Fields
// ============================================================================

/**
 * Compute a field value from a template string
 * Template format: "Hello {firstName} {lastName}"
 */
export function computeFieldValue(
  template: string,
  data: Record<string, unknown>
): string {
  return template.replace(/\{(\w+)\}/g, (_, fieldName) => {
    const value = data[fieldName];
    if (value === undefined || value === null) return '';
    return String(value);
  });
}

/**
 * Evaluate a condition expression
 * Condition format: "{fieldName} === 'value'" or "{fieldName}"
 */
export function evaluateCondition(
  condition: string,
  data: Record<string, unknown>
): boolean {
  try {
    // Replace field references with actual values
    const expression = condition.replace(/\{(\w+)\}/g, (_, fieldName) => {
      const value = data[fieldName];
      if (value === undefined || value === null) return 'undefined';
      if (typeof value === 'string') return JSON.stringify(value);
      return String(value);
    });

    // Simple boolean check for just a value
    if (expression === 'true') return true;
    if (expression === 'false' || expression === 'undefined' || expression === '""') return false;

    // Evaluate comparison expressions safely
    // eslint-disable-next-line no-new-func
    return new Function(`return (${expression})`)() as boolean;
  } catch {
    return true; // Default to showing field if condition is invalid
  }
}

// ============================================================================
// Request Status Helpers
// ============================================================================

/**
 * Check if a request is expired
 */
export function isRequestExpired(request: RequestTemplate): boolean {
  if (!request.expiresAt) return false;
  return new Date(request.expiresAt) < new Date();
}

/**
 * Check if a request can accept responses
 */
export function canAcceptResponses(request: RequestTemplate): boolean {
  if (request.status !== 'active') return false;
  if (isRequestExpired(request)) return false;
  return true;
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: RequestTemplate['status']): string {
  const labels: Record<RequestTemplate['status'], string> = {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    expired: 'Expired',
    completed: 'Completed',
  };
  return labels[status] || status;
}

// ============================================================================
// Field Helpers
// ============================================================================

/**
 * Create a new field definition with defaults
 */
export function createFieldDefinition(
  partial: Partial<FieldDefinition> & { type: FieldDefinition['type']; label: string }
): FieldDefinition {
  return {
    id: generateId('fld'),
    name: partial.name || toCamelCase(partial.label),
    label: partial.label,
    type: partial.type,
    required: partial.required ?? false,
    order: partial.order ?? 0,
    description: partial.description,
    placeholder: partial.placeholder,
    defaultValue: partial.defaultValue,
    options: partial.options,
    validation: partial.validation,
    computedTemplate: partial.computedTemplate,
    showWhen: partial.showWhen,
    subFields: partial.subFields,
  };
}

/**
 * Separate secure fields from regular data
 */
export function separateSecureFields(
  fields: FieldDefinition[],
  data: Record<string, unknown>
): { regularData: Record<string, unknown>; secureData: Record<string, string> } {
  const regularData: Record<string, unknown> = {};
  const secureData: Record<string, string> = {};

  for (const field of fields) {
    const value = data[field.name];
    if (value === undefined) continue;

    if (field.isSecure) {
      secureData[field.name] = String(value);
    } else {
      regularData[field.name] = value;
    }
  }

  return { regularData, secureData };
}
